import { useEffect, useMemo, useState } from 'react';
import { useSocket } from '@/lib/socket';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationContext } from '@/contexts/LocationContext';
import { rowMatchesState, selectedStateCode } from '@/lib/locationFilter';
import { Stage } from '@/components/operations/types';
import {
  deriveWorkOrderStatus,
  OperationSummary,
} from '@/components/work-orders/types';
import { buildWorkOrderConfig, RawWorkOrder as FullRawWO } from '@/components/work-orders/configMap';
import { deriveSalesOrderStatus, SalesOrderOutcome, SalesOrderRow, SalesOrderWorkOrder } from './types';
import {
  ApiSalesOrderSnapshot,
  listSalesOrderHistory,
} from '@/lib/salesOrderHistoryApi';

const HISTORY_LOOKBACK_DAYS = 90;
const MS_PER_DAY = 86_400_000;
const HISTORY_LIMIT = 5000;

type RawWorkOrder = FullRawWO;

interface RawSalesOrder {
  id: string;
  orderNumber: string;
  date: string;
  customer: string;
  location: string;
  workOrders?: RawWorkOrder[];
}

const parseSlashDate = (raw: string) => {
  if (!raw) return 0;
  const [m, d, y] = raw.split('/').map(Number);
  if (!m || !y) return 0;
  return new Date(y, (m || 1) - 1, d || 1).getTime();
};

const parseIsoTs = (s?: string) => {
  if (!s) return 0;
  const n = Date.parse(s);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (ts: number) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

function woOperations(wo: RawWorkOrder): OperationSummary[] {
  if (wo.isTKS0026) {
    return [
      { stage: 'Metal' as Stage, isComplete: wo.metalComplete },
      { stage: 'Assembly' as Stage, isComplete: wo.finalAssemblyComplete },
    ];
  }
  return [
    { stage: 'Kitting', isComplete: wo.kittingComplete },
    { stage: 'Fabric', isComplete: wo.fabricComplete },
    { stage: 'Metal', isComplete: wo.metalComplete },
    { stage: 'Assembly', isComplete: wo.finalAssemblyComplete },
  ];
}

function woAllComplete(wo: RawWorkOrder): boolean {
  if (wo.isTKS0026) return wo.metalComplete && wo.finalAssemblyComplete;
  return wo.kittingComplete && wo.fabricComplete && wo.metalComplete && wo.finalAssemblyComplete;
}

function buildSOWorkOrder(wo: RawWorkOrder): SalesOrderWorkOrder {
  const ops = woOperations(wo);
  const opsTotal = ops.length;
  const opsComplete = ops.filter((o) => o.isComplete).length;
  const isBuilt = !!wo.isBuilt;
  const allComplete = opsComplete === opsTotal;
  const status = deriveWorkOrderStatus(isBuilt ? 'Built' : 'Active', allComplete);
  return {
    id: wo.id,
    workOrderId: wo.orderID ?? '',
    displayName: wo.displayName ?? '',
    status,
    woStatusText: wo.status ?? '',
    isBuilt,
    isTKS0026: !!wo.isTKS0026,
    kittingComplete: !!wo.kittingComplete,
    fabricComplete: !!wo.fabricComplete,
    metalComplete: !!wo.metalComplete,
    finalAssemblyComplete: !!wo.finalAssemblyComplete,
    opsTotal,
    opsComplete,
    opsPercent: opsTotal === 0 ? 0 : Math.round((opsComplete / opsTotal) * 100),
    config: buildWorkOrderConfig(wo),
  };
}

function liveRow(so: RawSalesOrder): SalesOrderRow {
  const wos = so.workOrders ?? [];
  const workOrdersTotal = wos.length;
  const workOrdersComplete = wos.filter(woAllComplete).length;
  const allOps = wos.flatMap(woOperations);
  const opsTotal = allOps.length;
  const opsComplete = allOps.filter((o) => o.isComplete).length;
  const allComplete = workOrdersTotal > 0 && workOrdersComplete === workOrdersTotal;
  const outcome: SalesOrderOutcome = 'Active';
  return {
    id: so.id,
    orderNumber: so.orderNumber,
    customer: so.customer,
    location: so.location,
    date: so.date,
    dateTs: parseSlashDate(so.date),
    workOrdersTotal,
    workOrdersComplete,
    workOrdersPercent:
      workOrdersTotal === 0 ? 0 : Math.round((workOrdersComplete / workOrdersTotal) * 100),
    opsTotal,
    opsComplete,
    opsPercent: opsTotal === 0 ? 0 : Math.round((opsComplete / opsTotal) * 100),
    operations: allOps,
    workOrders: wos.map(buildSOWorkOrder),
    allComplete,
    outcome,
    status: deriveSalesOrderStatus(outcome, allComplete),
    completedAt: '',
    completedTs: 0,
  };
}

function historyRow(snap: ApiSalesOrderSnapshot): SalesOrderRow {
  // We don't have per-WO state in the SO snapshot — just the IDs. Treat the
  // SO as Completed if completedAt is set; counts default to 0 in that case.
  const completedTs = parseIsoTs(snap.completedAt);
  const outcome: SalesOrderOutcome = completedTs ? 'Completed' : 'Active';
  const allComplete = !!completedTs;
  return {
    id: snap.salesOrderInternalId,
    orderNumber: snap.orderNumber,
    customer: snap.customer,
    location: snap.location,
    date: snap.date,
    dateTs: parseSlashDate(snap.date),
    workOrdersTotal: snap.workOrderInternalIds?.length ?? 0,
    workOrdersComplete: 0,
    workOrdersPercent: 0,
    opsTotal: 0,
    opsComplete: 0,
    opsPercent: 0,
    operations: [],
    workOrders: [],
    allComplete,
    outcome,
    status: deriveSalesOrderStatus(outcome, allComplete),
    completedAt: fmtDate(completedTs),
    completedTs,
  };
}

export function useSalesOrdersData(): SalesOrderRow[] {
  const socket = useSocket();
  const { token } = useAuth();
  const { selectedLocation } = useLocationContext();
  const stateCode = selectedStateCode(selectedLocation);
  const [orders, setOrders] = useState<RawSalesOrder[]>([]);
  const [history, setHistory] = useState<ApiSalesOrderSnapshot[]>([]);

  useEffect(() => {
    if (!socket) return;
    const handleData = (data: RawSalesOrder[]) => {
      if (Array.isArray(data)) setOrders(data);
    };
    socket.on('netsuiteData', handleData);
    socket.emit('requestNetSuiteData');
    return () => {
      socket.off('netsuiteData', handleData);
    };
  }, [socket]);

  useEffect(() => {
    let cancelled = false;
    const from = new Date(Date.now() - HISTORY_LOOKBACK_DAYS * MS_PER_DAY).toISOString();
    listSalesOrderHistory(token, { completed: 'all', from, limit: HISTORY_LIMIT })
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch((err) => {
        console.error('Failed to load SO history', err);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return useMemo(() => {
    const byId = new Map<string, SalesOrderRow>();
    for (const snap of history) {
      byId.set(snap.salesOrderInternalId, historyRow(snap));
    }
    for (const so of orders) {
      byId.set(so.id, liveRow(so));
    }
    const all = Array.from(byId.values());
    if (!stateCode) return all;
    return all.filter((r) => rowMatchesState(r.location, stateCode));
  }, [orders, history, stateCode]);
}
